import { ref, computed, shallowRef } from 'vue'
import { initializeApp, getApp, deleteApp } from 'firebase/app'
import {
  getFirestore,
  terminate,
  collection as firestoreCollection,
  doc as firestoreDocument,
  initializeFirestore,
  memoryLocalCache,
  query,
  onSnapshot,
  where,
} from 'firebase/firestore'
import { getAuth, signInWithCustomToken, signOut } from 'firebase/auth'
import { useDebounce, toValue } from '@vueuse/core'

import { useFirestore } from './firestore-sdk-wrapper.js'

import { isNil, is } from 'ramda'
import { useLogger } from '@booksy-com/vue-tools'
import { useAuthentication } from '@/composables/authentication/index.js'
import { useEppo } from '@/composables/eppo'

import { transformKeysToCamelCase } from '@/lib/helpers.js'

import { getFirestoreConfigForWeb } from './api/index.js'

import {
  createPath,
  hasNullOrUndefined,
} from './utils/index.js'

import {
  COLLECTIONS,
} from './collections/consts.js'

import {
  FEATURE_FLAGS,
} from '@/lib/consts.js'

let FIRESTORE_DB
let FIRESTORE_APP_NAME

const isInitialized = ref(false)
let _unsubscribeUserPermissonChanges
let _checkFeatureFlagStateInterval

const FF_CHECK_INTERVAL_MS = 60000 // 1m

const logger = useLogger('booksy-firestore')

export function useBooksyFirestore () {
  const _getConfig = async () => {
    logger.info('configuration fetching...')
    try {
      const response = await getFirestoreConfigForWeb()

      return response?.data
    } catch (e) {
      logger.info('configuration fetching failed')
      logger.error(e.message)
      throw new Error()
    }
  }

  const _initFirestore = async ({ config }) => {
    logger.info('initialize firebase app...')
    try {
      const firestoreApp = initializeApp(transformKeysToCamelCase(config))
      FIRESTORE_APP_NAME = firestoreApp.name
      FIRESTORE_DB = initializeFirestore(firestoreApp, {
        localCache: memoryLocalCache(),
      }, config.database)

      logger.info('firebase app initialized')
    } catch (e) {
      logger.info('firebase application initialization failed')
      logger.error(e.message)
      throw new Error()
    }
  }

  const _authorize = async ({ token }) => {
    logger.info('authorize with token...')
    try {
      const firestoreApp = getApp(FIRESTORE_APP_NAME)
      await signInWithCustomToken(getAuth(firestoreApp), token)
      logger.info('authorized')
    } catch (e) {
      logger.info('firebase authorization failed')
      logger.error(e.message)
      throw new Error()
    }
  }

  const _handleChangingLoggedUserPermissions = (context) => {
    _unsubscribeUserPermissonChanges = onSnapshot(query(firestoreCollection(FIRESTORE_DB,
      createPath(COLLECTIONS.RESOURCES, { businessId: context?.business.id })
    ), where('staff_user_id', '==', context?.user.id)), (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (!['modified', 'removed'].includes(change.type)) return

        const userStafferAccessLevel = change.doc.get('staff_access_level')
        const userStafferDeleted = change.doc.get('deleted')
        if (userStafferAccessLevel !== context?.user.access_level
          || !isNil(userStafferDeleted)
          || change.type === 'removed'
        ) {
          useAuthentication().logout()
        }
      })
    })
  }

  const _startCheckingFeatureFlagStatus = () => {
    _checkFeatureFlagStateInterval = setInterval(() => {
      if (useEppo().ff(FEATURE_FLAGS.Feature_ProviderFirestore)) return

      destroy()
    }, FF_CHECK_INTERVAL_MS)
  }

  const _stopCheckingFeatureFlagStatus = () => {
    if (_checkFeatureFlagStateInterval) {
      clearInterval(_checkFeatureFlagStateInterval)
      _checkFeatureFlagStateInterval = undefined
    }
  }

  const init = async (context) => {
    if (isInitialized.value) {
      logger.info('firestore already initialized... skip it')
      return
    }

    try {
      const config = await _getConfig()
      await _initFirestore(config)
      await _authorize(config)
      _handleChangingLoggedUserPermissions?.(context)
      _startCheckingFeatureFlagStatus()
      isInitialized.value = true
      logger.info('ready')
    } catch (e) {
      logger.error('firestore failed to initialize due to errors')
    }
  }

  const destroy = async () => {
    logger.info('destroying...')

    if (!isInitialized.value) {
      logger.info('not initalized... skip it')
      return
    }

    try {
      _unsubscribeUserPermissonChanges?.()
      _stopCheckingFeatureFlagStatus()
      const firestoreApp = getApp(FIRESTORE_APP_NAME)
      await signOut(getAuth(firestoreApp))
      const firestoreInstance = getFirestore(firestoreApp)
      await terminate(firestoreInstance)
      await deleteApp(firestoreApp)
      isInitialized.value = false
      logger.info('firestore app destroyed')
    } catch (e) {
      logger.error(e.message)
    }
  }

  const _defaultErrorHandler = (error, path) => {
    if (is(String, error?.message) && error?.message.includes('Firestore shutting down')) {
      logger.warn('Firestore shutting down')
      return
    }

    logger.error(`error on ${path}:`, error)
  }

  const _getFirestoreData = (type, path, options = {}) => {
    const { data, changes } = useFirestore(computed(() => {
      if (!isInitialized.value) return

      const params = toValue(options?.params)
      if (hasNullOrUndefined(params)) return

      return query(type(FIRESTORE_DB,
        createPath(path, params)
      ), ...toValue((options?.query || [])))
    }), options?.initialValue, {
      autoDispose: false,
      errorHandler: options.onError,
    })

    return {
      data: useDebounce(data, 200),
      changes: useDebounce(changes, 200),
    }
  }

  const _addErrorHandler = (path, options, hasFailed) => {
    return {
      ...options,
      onError: (error) => {
        is(Function, options?.onError) && options.onError(error)

        if (options.setUnreadyOnError) hasFailed.value = true
        _defaultErrorHandler(error, path)
      },
    }
  }

  const getCollection = (collectionPath, options = {}) => {
    const transform = is(Function, options.transform) ? options.transform : (c) => c
    const hasFailed = shallowRef(false)
    const _options = _addErrorHandler(collectionPath, options, hasFailed)

    const { data: collection, changes } = _getFirestoreData(firestoreCollection, collectionPath, _options)

    return {
      collection: computed(() => transform(collection.value?.map((i) => ({ ...i, id: i.id })))),
      changes: computed(() => changes.value),
      isReady: computed(() => isInitialized.value && !isNil(collection.value) && !toValue(hasFailed)),
    }
  }

  const getDocument = (documentPath, options = {}) => {
    const transform = is(Function, options.transform) ? options.transform : (d) => d
    const hasFailed = shallowRef(false)
    const _options = _addErrorHandler(documentPath, options, hasFailed)

    const { data: doc } = _getFirestoreData(firestoreDocument, documentPath, _options)

    return {
      doc: computed(() => transform((doc.value && { ...doc.value, id: doc.value?.id }))),
      isReady: computed(() => isInitialized.value && !isNil(doc.value) && !toValue(hasFailed)),
    }
  }

  return {
    init,
    destroy,
    isInitialized: computed(() => isInitialized.value),
    getCollection,
    getDocument,
  }
}
